import React, { useEffect, useRef } from 'react';
import { useDispatch, useSelector } from 'react-redux';

import { addErrorMessageBody, toggleSpinner } from 'apps/auth/actions/appAction';
import { changeFormFieldValue } from 'apps/auth/actions/formAction';
import { Icon } from 'apps/auth/components/common/Icon/Icon';
import { Row } from 'apps/auth/components/common/Layout/Row';
import PopupButton from 'apps/auth/components/common/Popup/PopupButton';
import PopupNew, { type PopupNewHandle } from 'apps/auth/components/common/Popup/PopupNew';
import SimplePopup from 'apps/auth/components/common/Popup/SimplePopup';
import { Text } from 'apps/auth/components/common/Text/Text';
import { getField } from 'apps/auth/components/form-multi/utils';
import { OFAKIM_SEND_FILE } from 'apps/auth/constants/UrlConstants';
import { apiFetchFormData } from 'apps/auth/helpers/api';
import { logWarn } from 'apps/auth/helpers/clientLogger';
import type { AppDispatch, RootState } from 'apps/auth/redux/store';

import FileUploadPopup from './FileUploadPopup';
import { ImageContainer, InputContainer, ReplaceContainer } from './styles';

type FieldInfo = {
    name: string;
    value?: string | null;
    info?: React.ReactNode;
};

type MessagesSlice = {
    pageMessages?: {
        removePhoto?: string;
        replacePhoto?: string;
    };
};

interface OwnProps {
    formName: string;
    field: FieldInfo;
}

type AddPhotoProps = OwnProps;

const MAX_FILE_SIZE = 5 * 1024 * 1024; // 5MB
type UploadResponse = {
    result?: 'OK' | 'ERROR';
    message?: string;
};

const AddPhotoComponent: React.FC<AddPhotoProps> = ({ formName, field }) => {
    const dispatch = useDispatch<AppDispatch>();
    const removePhoto = useSelector((state: RootState) => (state.messages as MessagesSlice | undefined)?.pageMessages?.removePhoto);
    const replacePhoto = useSelector((state: RootState) => (state.messages as MessagesSlice | undefined)?.pageMessages?.replacePhoto);
    const inputFileRef = useRef<HTMLInputElement | null>(null);
    const fileRef = useRef<File | null>(null);
    const imageUrlRef = useRef<string | null>(null);
    const popupRef = useRef<PopupNewHandle | null>(null);

    useEffect(() => {
        return () => {
            if (imageUrlRef.current) {
                URL.revokeObjectURL(imageUrlRef.current);
            }
        };
    }, []);

    const changeFieldValue = (value: string | null) => {
        void dispatch(changeFormFieldValue(formName, value, field.name));
    };

    const handleSpinner = (isVisible: boolean) => {
        void dispatch(toggleSpinner(isVisible));
    };

    const showErrorMessage = (message: string) => {
        void dispatch(addErrorMessageBody(message));
    };

    const clearFieldEvent = (event: React.MouseEvent<HTMLDivElement>) => {
        event.preventDefault();
        popupRef.current?.open(
            <SimplePopup withoutBorder popupRef={popupRef}>
                <PopupButton
                    iconName="cross"
                    text={removePhoto}
                    onClick={() => {
                        changeFieldValue('');
                        popupRef.current?.close();
                    }}
                />
            </SimplePopup>,
        );
    };

    const onChangeHandler = (event: React.ChangeEvent<HTMLInputElement>) => {
        event.preventDefault();
        event.stopPropagation();

        const selectedFile = event.target.files?.[0];
        if (!selectedFile) {
            return;
        }

        const size = selectedFile.size ?? (selectedFile as unknown as { fileSize?: number })?.fileSize ?? 0;
        if (size > MAX_FILE_SIZE) {
            logWarn('Max file size : 5Mb');
            showErrorMessage('Max file size : 5Mb');
            return;
        }

        fileRef.current = selectedFile;
        imageUrlRef.current = URL.createObjectURL(selectedFile);
        popupRef.current?.open(<FileUploadPopup popupRef={popupRef} file={selectedFile} uploadFile={uploadFile} imageUrl={imageUrlRef.current} />);
        // Reset input to allow uploading the same file again.
        event.target.value = '';
    };

    const uploadFile = (event: React.MouseEvent<HTMLElement>) => {
        event.preventDefault();
        popupRef.current?.close();

        const currentFile = fileRef.current;
        if (!currentFile) {
            return;
        }

        handleSpinner(true);
        // getField типизирован под string, поэтому приводим второй аргумент к any.
        const safeGetField = getField as (...args: any[]) => ReturnType<typeof getField>;
        void apiFetchFormData<UploadResponse>(OFAKIM_SEND_FILE, { file: safeGetField('file', currentFile) })
            .then((json) => {
                if (json?.result === 'OK') {
                    popupRef.current?.close();
                    changeFieldValue(imageUrlRef.current ?? '');
                } else if (json?.result === 'ERROR') {
                    showErrorMessage(json.message ?? '');
                }
            })
            .catch((error) => {
                logWarn(error);
            })
            .finally(() => {
                fileRef.current = null;
                handleSpinner(false);
            });
    };

    const fieldValue = field.value ?? '';

    return (
        <>
            <InputContainer imgUrl={fieldValue}>
                <Row
                    indent="16px"
                    onClick={(event) => {
                        event.stopPropagation();
                    }}
                >
                    <Icon name="plus" color="primary" />
                </Row>
                {field.info}
                <input type="file" name="file" className="hidden" onChange={onChangeHandler} ref={inputFileRef} />
            </InputContainer>
            <ImageContainer dir="" imgUrl={fieldValue} onClick={clearFieldEvent}>
                <ReplaceContainer>
                    <Row indent="16px">
                        <Icon name="retry" color="white" />
                    </Row>
                    <Text size="main" color="white">
                        {replacePhoto}
                    </Text>
                </ReplaceContainer>
            </ImageContainer>
            <PopupNew ref={popupRef} overflowHidden />
        </>
    );
};

export default AddPhotoComponent;
